// Organisation logo uploads (Profile & Branding, and the Falcon Console): checks the file, turns an SVG into a
// PNG in the browser (so no SVG is ever stored or served), and previews the logo as it will look in the sidebar.
import { esc, readFileAsDataUrl } from "./lib.js";
import { sidebarLogoHtml } from "./shell.js";

export const LOGO_ACCEPT = "image/png,image/jpeg,image/webp,image/svg+xml,.svg";
export const LOGO_HINT = "PNG, JPG, WEBP or SVG, up to 1 MB. Best: a square PNG or SVG, at least 128 × 128 px. Other shapes are scaled down to fit.";

/** A data URL ready to upload, or an error message for the person. */
export async function prepareLogo(file: File): Promise<string> {
  if (file.size > 1024 * 1024) throw new Error("The logo must be 1 MB or smaller.");
  const svg = file.type === "image/svg+xml" || /\.svg$/i.test(file.name);
  if (!svg && !["image/png", "image/jpeg", "image/webp"].includes(file.type)) throw new Error("Use a PNG, JPG, WEBP or SVG file.");
  if (!svg) return readFileAsDataUrl(file);
  const url = URL.createObjectURL(new Blob([await file.text()], { type: "image/svg+xml" }));
  try {
    const img = await new Promise<HTMLImageElement>((ok, no) => {
      const i = new Image();
      i.onload = () => ok(i);
      i.onerror = () => no(new Error("That SVG couldn't be read. Try saving it again, or use a PNG."));
      i.src = url;
    });
    // SVGs without a size default to 300 × 150 in browsers; draw at a sharp size, keeping the shape.
    const w0 = img.naturalWidth || 600, h0 = img.naturalHeight || 200;
    const scale = Math.min(4, 1200 / Math.max(w0, h0));
    const c = document.createElement("canvas");
    c.width = Math.max(1, Math.round(w0 * scale));
    c.height = Math.max(1, Math.round(h0 * scale));
    c.getContext("2d")!.drawImage(img, 0, 0, c.width, c.height);
    return c.toDataURL("image/png");
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** A slice of the sidebar at its real width, with this logo in it (on a white box, or transparent). */
export function sidebarPreviewHtml(src: string | null, orgName: string, transparent = false): string {
  return `<div class="sidebar-logo-preview"><small>In the sidebar</small><span class="sidebar-logo-preview-brand">FALCON PLM</span>${src ? sidebarLogoHtml(src, "Logo preview", transparent) : '<span class="muted">No logo: just the name</span>'}<strong>${esc(orgName.toUpperCase())}</strong></div>`;
}

/** Put the preview into `holder`. */
export function showSidebarPreview(holder: HTMLElement | null, src: string | null, orgName: string, transparent = false): void {
  if (holder) holder.innerHTML = sidebarPreviewHtml(src, orgName, transparent);
}

/** The "Transparent background" tick box shown next to a logo upload. */
export const transparentBox = (checked: boolean, change: string, disabled = false) =>
  `<label class="check-row logo-transparent-box"><input type="checkbox" name="logoTransparent" data-change="${change}" ${checked ? "checked" : ""} ${disabled ? "disabled" : ""}><span>Transparent background <small class="muted">(no white box behind the logo: for PNGs with a transparent background)</small></span></label>`;
